'use client';

import type { PointerEvent, ReactNode } from 'react';
import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { Assign } from '@ark-ui/react';
import { Tooltip as ArkTooltip } from '@ark-ui/react';
import { type TooltipVariantProps, tooltip } from '@terra/ui-styled-system';
import type { HTMLStyledProps } from '@terra/ui-styled-system';
import { VStack } from '@terra/ui-styled-system';
import { createStyleContext } from './utils/create-style-context';
import { Text } from '../text';

const { withRootProvider, withContext } = createStyleContext(tooltip);

const TooltipRootWithDefaults = (
  props: Assign<ArkTooltip.RootProps, TooltipVariantProps>,
) => {
  return <ArkTooltip.Root openDelay={props.openDelay ?? 100} {...props} />;
};

const Root = withRootProvider<
  Assign<ArkTooltip.RootProps, TooltipVariantProps>
>(TooltipRootWithDefaults);

const Content = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkTooltip.ContentProps>
>(ArkTooltip.Content, 'content');

const Positioner = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, ArkTooltip.PositionerProps>
>(ArkTooltip.Positioner, 'positioner');

const Trigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, ArkTooltip.TriggerProps>
>(ArkTooltip.Trigger, 'trigger');

export type TooltipProps = {
  title?: string;
  customContent?: ReactNode;
  subtitle?: string;
  children: ReactNode;
  positioning?: ArkTooltip.RootProps['positioning'];
  openDelay?: number;
  closeDelay?: number;
  disabled?: boolean;
  maxW?: string;
  allowOverflow?: boolean;
  interactive?: boolean;
  closeOnScroll?: boolean;
  whiteSpace?: 'normal' | 'pre-line';
  anchorToPointerOnEnter?: boolean;
  followCursor?: boolean;
};

export const Tooltip = ({
  title,
  subtitle,
  children,
  positioning,
  openDelay = 100,
  closeDelay,
  customContent,
  disabled = false,
  maxW,
  allowOverflow = false,
  interactive = false,
  closeOnScroll,
  whiteSpace = 'normal',
  anchorToPointerOnEnter,
  followCursor,
}: TooltipProps) => {
  const anchorRect = useRef<DOMRect | null>(null);
  const getAnchorRect = useCallback(() => anchorRect.current, []);
  const useVirtualAnchor = anchorToPointerOnEnter || followCursor;

  if (disabled || (!title && !customContent)) {
    return children;
  }

  const setAnchorFromPointer = (event: PointerEvent<HTMLElement>) => {
    anchorRect.current = new DOMRect(event.clientX, event.clientY, 1, 1);
  };

  const tooltipContent = (
    <Positioner
      zIndex="tooltip"
      style={{
        pointerEvents: customContent ? 'auto' : 'none',
      }}
    >
      <Content
        maxW={maxW}
        w={maxW === 'full' ? 'full' : undefined}
        maxH={allowOverflow ? 'none' : undefined}
        overflow={allowOverflow ? 'visible' : undefined}
        p={customContent ? 3 : undefined}
        style={customContent ? { pointerEvents: 'auto' } : undefined}
      >
        {customContent ? (
          customContent
        ) : (
          <VStack
            gap={1}
            alignItems="flex-start"
            maxW="full"
            overflow="visible"
            minW="0"
          >
            <Text
              fontSize="xs"
              fontWeight="400"
              color="fg.tooltip.default"
              lineHeight="tight"
              wordBreak="break-word"
              overflowWrap="break-word"
              whiteSpace={whiteSpace}
              overflow="visible"
            >
              {title}
            </Text>
            {subtitle && (
              <Text
                fontSize="xs"
                fontWeight="300"
                color="fg.tooltip.muted"
                lineHeight="tight"
                wordBreak="break-word"
                overflowWrap="break-word"
                whiteSpace={whiteSpace}
                overflow="visible"
              >
                {subtitle}
              </Text>
            )}
          </VStack>
        )}
      </Content>
    </Positioner>
  );

  return (
    <Root
      positioning={{
        strategy: 'fixed',
        ...(followCursor ? { gutter: 4, placement: 'top-start' } : {}),
        ...positioning,
        ...(useVirtualAnchor ? { getAnchorRect } : {}),
      }}
      interactive={interactive || !!customContent}
      openDelay={openDelay}
      closeDelay={closeDelay ?? (customContent ? 300 : undefined)}
      closeOnScroll={closeOnScroll ?? (allowOverflow ? false : undefined)}
    >
      <ArkTooltip.Context>
        {(tooltip) => (
          <>
            <Trigger
              asChild
              {...(anchorToPointerOnEnter
                ? {
                    onPointerEnter: (event: PointerEvent<HTMLElement>) => {
                      setAnchorFromPointer(event);
                      tooltip.reposition();
                    },
                  }
                : {})}
              {...(followCursor
                ? {
                    onPointerMove: (event: PointerEvent<HTMLElement>) => {
                      setAnchorFromPointer(event);
                      tooltip.reposition();
                    },
                  }
                : {})}
            >
              {children}
            </Trigger>
            {tooltip.open && typeof document !== 'undefined'
              ? createPortal(tooltipContent, document.body)
              : null}
          </>
        )}
      </ArkTooltip.Context>
    </Root>
  );
};
